import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';

/**
 * Content structure for the NoEventsCard component
 */
export interface NoEventsCardContent {
  /**
   * Card title - typically "No events at this time"
   */
  title: string;
  /**
   * Message text explaining when events will be posted
   */
  message: string;
}

export interface NoEventsCardProps {
  /**
   * Content object containing title and message text
   */
  content: NoEventsCardContent;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * NoEventsCard - Empty state card displayed when no events are scheduled
 *
 * Shows a clear message to users when browsing a month with no scheduled events.
 * Features a distinctive red/dark left border accent.
 *
 * @example
 * ```tsx
 * <NoEventsCard
 *   content={{
 *     title: "No events at this time",
 *     message: "There are no events scheduled for this month. New events will be posted soon—please check back."
 *   }}
 * />
 * ```
 */
export const NoEventsCard = forwardRef<HTMLDivElement, NoEventsCardProps>(
  ({ content, className }, ref) => {
    if (!content) {
      return null;
    }

    const { title, message } = content;

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          'rounded bg-background border border-border',
          'border-l-4 border-l-[#8B0000]', // Dark red left accent border
          'p-4',
          className
        )}
      >
        {/* Title */}
        <h3 className={cn(typography.subtitle, 'text-foreground mb-2')}>
          {title}
        </h3>

        {/* Message */}
        <p className={cn(typography.body, 'text-muted-foreground')}>
          {message}
        </p>
      </div>
    );
  }
);

NoEventsCard.displayName = 'NoEventsCard';
